import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/lib/currency";

export default function TenantDashboard() {
  const { t } = useTranslation();
  const { fmt } = useCurrency();
  const [properties, setProperties] = useState([]);
  const [busy, setBusy] = useState(null);
  const { refresh } = useAuth();

  const load = useCallback(async () => {
    const { data } = await api.get("/tenant/properties");
    setProperties(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const payRent = async (pid) => {
    setBusy(pid);
    try {
      const { data } = await api.post("/tenant/pay-rent", { property_id: pid });
      toast.success(`#${data.month_index}`);
      await load(); await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12">
      <div className="font-mono-label text-[11px] text-[#002FA7]">⎯⎯ {t("ten_tag")}</div>
      <h1 className="font-display text-5xl font-black mt-2">{t("ten_title")}</h1>

      {properties.length === 0 ? (
        <div className="border border-black/10 p-16 text-center mt-10">
          <div className="font-display text-2xl font-bold">{t("ten_empty_title")}</div>
          <div className="text-zinc-500 text-sm mt-2">{t("ten_empty_sub")}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          {properties.map((p) => (
            <div key={p.id} className="border border-black/10 bg-white overflow-hidden">
              <div className="aspect-[16/9]">
                <img src={p.image_url} alt="" className="w-full h-full object-cover"/>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-xl font-bold truncate">{p.name}</div>
                    <div className="text-xs text-zinc-500 mt-1 truncate">{p.address}</div>
                  </div>
                  <Badge className="rounded-none bg-black font-mono-label text-[10px] shrink-0">{t("ten_tag")}</Badge>
                </div>

                <div className="grid grid-cols-3 gap-px bg-black/10">
                  <div className="bg-white p-3 min-w-0">
                    <div className="font-mono-label text-[9px] text-zinc-500 truncate">{t("ten_rent_label")}</div>
                    <div className="font-display text-base lg:text-lg font-bold tabular-nums truncate">{fmt(p.monthly_rent)}</div>
                  </div>
                  <div className="bg-white p-3 min-w-0">
                    <div className="font-mono-label text-[9px] text-zinc-500 truncate">{t("ten_last_paid")}</div>
                    <div className="font-display text-base lg:text-lg font-bold tabular-nums">{p.last_payment ? `#${p.last_payment.month_index}` : "—"}</div>
                  </div>
                  <div className="bg-white p-3 min-w-0">
                    <div className="font-mono-label text-[9px] text-zinc-500 truncate">{t("ten_next")}</div>
                    <div className="font-display text-base lg:text-lg font-bold text-[#002FA7] tabular-nums">#{p.next_month_index}</div>
                  </div>
                </div>

                <Button data-testid={`pay-rent-${p.id}`} onClick={() => payRent(p.id)} disabled={busy === p.id}
                  className="w-full rounded-none bg-[#002FA7] hover:bg-[#001F70] h-11 font-mono-label text-xs">
                  {busy === p.id ? t("processing") : t("ten_pay_btn", { amount: fmt(p.monthly_rent), n: p.next_month_index })}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
